import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Gift, TrendingUp, Zap, Star } from "lucide-react";

interface CartIncentive {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  threshold?: number;
  currentValue: number;
  type: "achievement" | "progress" | "unlock";
  color: string;
}

interface CartIncentivesProps {
  packageCount: number;
  totalAmount: number;
  eventCount: number;
}

export function CartIncentives({ packageCount, totalAmount, eventCount }: CartIncentivesProps) {
  const incentives: CartIncentive[] = [];

  // Multi-event booking incentive
  if (eventCount >= 2) {
    incentives.push({
      id: "multi_event",
      title: "Multi-Event Booking Active!",
      description: "You're saving 12% by booking multiple events together",
      icon: <TrendingUp className="h-4 w-4" />,
      currentValue: eventCount,
      type: "achievement",
      color: "from-green-500 to-emerald-600"
    });
  } else if (eventCount === 1) {
    incentives.push({
      id: "multi_event_progress",
      title: "Add Another Event & Save 12%",
      description: "Book 2+ events together for automatic bundle discount",
      icon: <Gift className="h-4 w-4" />,
      currentValue: eventCount,
      threshold: 2,
      type: "progress",
      color: "from-blue-500 to-blue-600"
    });
  }

  // High-value booking incentive
  if (totalAmount >= 10000) {
    incentives.push({
      id: "premium_booking",
      title: "Premium Client Status",
      description: "Complimentary rehearsal session & priority scheduling",
      icon: <Star className="h-4 w-4" />,
      currentValue: totalAmount,
      type: "achievement",
      color: "from-purple-500 to-purple-600"
    });
  } else if (totalAmount >= 7500) {
    incentives.push({
      id: "premium_progress",
      title: `$${(10000 - totalAmount).toLocaleString()} to Premium Status`,
      description: "Unlock complimentary rehearsal & priority scheduling",
      icon: <Zap className="h-4 w-4" />,
      currentValue: totalAmount,
      threshold: 10000,
      type: "progress",
      color: "from-amber-500 to-orange-600"
    });
  }

  // Multi-moment incentive
  const totalPackages = packageCount;
  if (totalPackages >= 3) {
    incentives.push({
      id: "multi_moment",
      title: "Multi-Moment Excellence",
      description: "Creating a complete experience across your event timeline",
      icon: <Gift className="h-4 w-4" />,
      currentValue: totalPackages,
      type: "achievement",
      color: "from-pink-500 to-rose-600"
    });
  }

  // Early booking incentive (always show to encourage action)
  if (totalAmount > 0) {
    incentives.push({
      id: "book_now",
      title: "Limited Time Availability",
      description: "Lock in your date & prices - top musicians book fast",
      icon: <Zap className="h-4 w-4" />,
      currentValue: 1,
      type: "unlock",
      color: "from-red-500 to-red-600"
    });
  }

  if (incentives.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {incentives.map((incentive) => (
        <Card
          key={incentive.id}
          className={`border-2 bg-gradient-to-r ${incentive.color} text-white overflow-hidden`}
        >
          <CardContent className="p-3 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                {incentive.icon}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="font-semibold text-sm leading-tight">{incentive.title}</h4>
                {incentive.type === "achievement" && (
                  <Badge className="bg-white/30 text-white border-white/50 text-xs flex-shrink-0">
                    Active
                  </Badge>
                )}
              </div>
              <p className="text-xs text-white/90 leading-snug">{incentive.description}</p>
              
              {incentive.type === "progress" && incentive.threshold && (
                <div className="mt-2">
                  <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-white h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min((incentive.currentValue / incentive.threshold) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
